// Editor-gated correction modal for SOW status. The standard PUT path
// enforces the SOW state-machine guard (draft → active → completed and
// to-cancelled at any time, but no reverse transitions). This surface
// bypasses the guard for legitimate corrections — e.g. a SOW
// mis-cancelled by a clerical error needs to be re-Activated. The
// required reason field plus the audit row is the control.

import { useState } from "react";
import Modal from "../../../common/Modal";
import Button from "../../../common/Button";
import Alert from "../../../common/Alert";
import { ApiError } from "../../../../services/api";
import type { Sow, SowStatus } from "../../../../types";

const MIN_REASON = 10;
const MAX_REASON = 500;

const STATUS_OPTIONS: SowStatus[] = ["draft", "active", "completed", "cancelled"];

const STATUS_LABEL: Record<SowStatus, string> = {
  draft: "Draft",
  active: "Active",
  completed: "Completed",
  cancelled: "Cancelled",
};

interface CorrectSowStatusModalProps {
  isOpen: boolean;
  onClose: () => void;
  sow: Sow | null;
  onConfirm: (newStatus: SowStatus, reason: string) => Promise<unknown>;
  isSubmitting?: boolean;
}

export default function CorrectSowStatusModal({
  isOpen,
  onClose,
  sow,
  onConfirm,
  isSubmitting,
}: CorrectSowStatusModalProps) {
  const [status, setStatus] = useState<SowStatus>("draft");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  // Pattern 0: seed fields from the target SOW when the modal opens so a
  // previous correction attempt does not persist across invocations.
  const seedKey = isOpen && sow ? sow.id : null;
  const [seededFor, setSeededFor] = useState<string | null>(null);
  if (seedKey !== null && seedKey !== seededFor) {
    setSeededFor(seedKey);
    setStatus(sow!.status);
    setReason("");
    setError(null);
  }
  if (!isOpen && seededFor !== null) {
    setSeededFor(null);
  }

  const statusChanged = sow !== null && status !== sow.status;
  const reasonValid =
    reason.trim().length >= MIN_REASON && reason.length <= MAX_REASON;
  const canSubmit = !isSubmitting && sow !== null && statusChanged && reasonValid;

  const handleConfirm = async () => {
    if (!canSubmit) return;
    setError(null);
    try {
      await onConfirm(status, reason.trim());
      onClose();
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
        return;
      }
      setError(
        err instanceof Error ? err.message : "Failed to correct status.",
      );
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        sow ? `Correct status of ${sow.number}?` : "Correct SOW status"
      }
    >
      <div className="space-y-4" data-testid="correct-sow-status-modal">
        <Alert variant="warning">
          This action is logged to the audit trail and bypasses the
          standard SOW lifecycle. Use it only to repair clerical errors.
        </Alert>
        {sow && (
          <dl className="grid grid-cols-2 gap-3 rounded-md bg-slate-50 dark:bg-slate-800/60 p-3">
            <div>
              <dt className="text-caption text-slate-500 dark:text-slate-400">
                SOW
              </dt>
              <dd className="text-body text-slate-900 dark:text-slate-100">
                {sow.name}
              </dd>
            </div>
            <div>
              <dt className="text-caption text-slate-500 dark:text-slate-400">
                Current status
              </dt>
              <dd className="text-body text-slate-900 dark:text-slate-100">
                {STATUS_LABEL[sow.status]}
              </dd>
            </div>
          </dl>
        )}
        <div>
          <label htmlFor="correct-sow-status-select" className="label">
            New status
          </label>
          <select
            id="correct-sow-status-select"
            data-testid="correct-sow-status-select"
            className="input"
            value={status}
            onChange={(e) => setStatus(e.target.value as SowStatus)}
            disabled={isSubmitting}
          >
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {STATUS_LABEL[s]}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="correct-sow-status-reason" className="label">
            Reason
          </label>
          <textarea
            id="correct-sow-status-reason"
            data-testid="correct-sow-status-reason"
            className="input min-h-[80px]"
            value={reason}
            maxLength={MAX_REASON}
            onChange={(e) => setReason(e.target.value)}
            disabled={isSubmitting}
            required
          />
          <p className="mt-1 text-caption text-slate-500 dark:text-slate-400">
            {reason.length}/{MAX_REASON} (minimum {MIN_REASON})
          </p>
        </div>
        {error && <Alert variant="error">{error}</Alert>}
        <div className="flex justify-end gap-3 pt-2">
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirm}
            loading={isSubmitting}
            disabled={!canSubmit}
            data-testid="correct-sow-status-submit"
          >
            Save correction
          </Button>
        </div>
      </div>
    </Modal>
  );
}
